// components/settings/PaymentMethods.tsx
import React, { useState } from "react";
import {
  CreditCard,
  Building2,
  Plus,
  Star,
  Trash2,
  MoreVertical,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { IPaymentMethod } from "../../lib/types/authOnboarding";

interface PaymentMethodsProps {
  paymentMethods: IPaymentMethod[];
  onAddNew: () => void;
  onSetDefault: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  isSettingDefault?: boolean;
  isDeletingPayment?: boolean;
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({
  paymentMethods,
  onAddNew,
  onSetDefault,
  onDelete,
  isSettingDefault,
  isDeletingPayment,
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  const handleSetDefault = async (id: string) => {
    setSettingDefaultId(id);
    try {
      await onSetDefault(id);
    } finally {
      setSettingDefaultId(null);
      setOpenMenuId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
      setOpenMenuId(null);
    }
  };

  const getPaymentIcon = (type: string) => {
    switch (type) {
      case "bank":
        return Building2;
      case "paypal":
        return CreditCard;
      default:
        return CreditCard;
    }
  };

  const getPaymentTitle = (method: IPaymentMethod) => {
    if (method.type === "bank") {
      return method.bankName || "Bank Account";
    }
    return "PayPal";
  };

  const getPaymentSubtitle = (method: IPaymentMethod) => {
    if (method.type === "bank") {
      const accountType = method.accountType
        ? method.accountType.charAt(0).toUpperCase() +
          method.accountType.slice(1)
        : "Account";
      return `${accountType} •••• ${method.accountLastFour || "****"}`;
    }
    return method.paypalEmail || "PayPal Account";
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Payment Methods
            </h2>
            <p className="text-sm text-gray-500">
              Manage how you receive payments
            </p>
          </div>
        </div>
        <button
          onClick={onAddNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 text-sm"
        >
          <Plus className="w-4 h-4" />
          Add New
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {paymentMethods.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <CreditCard className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No payment methods
            </h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              Add a payment method to receive your earnings via direct deposit
              or PayPal.
            </p>
            <button
              onClick={onAddNew}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl transition-all text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Payment Method
            </button>
          </div>
        ) : (
          /* Payment Methods List */
          <div className="space-y-3">
            {paymentMethods.map((method) => {
              const Icon = getPaymentIcon(method.type);
              const isDeleting = deletingId === method._id;
              const isSettingAsDefault = settingDefaultId === method._id;
              const isProcessing = isDeleting || isSettingAsDefault;

              return (
                <div
                  key={method._id}
                  className={`relative flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                    method.isDefault
                      ? "border-[#8B52FF]/30 bg-[#8B52FF]/5"
                      : "border-gray-100 hover:border-gray-200"
                  } ${isProcessing ? "opacity-60" : ""}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        method.type === "bank" ? "bg-blue-100" : "bg-indigo-100"
                      }`}
                    >
                      <Icon
                        className={`w-6 h-6 ${
                          method.type === "bank"
                            ? "text-blue-600"
                            : "text-indigo-600"
                        }`}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">
                          {getPaymentTitle(method)}
                        </p>
                        {method.isDefault && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#8B52FF]/10 text-[#8B52FF] text-xs font-medium rounded-full">
                            <Star className="w-3 h-3" />
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {getPaymentSubtitle(method)}
                      </p>
                    </div>
                  </div>

                  {/* Actions Menu */}
                  <div className="relative">
                    <button
                      onClick={() =>
                        setOpenMenuId(
                          openMenuId === method._id ? null : method._id
                        )
                      }
                      disabled={isProcessing}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? (
                        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                      ) : (
                        <MoreVertical className="w-5 h-5 text-gray-400" />
                      )}
                    </button>

                    {openMenuId === method._id && !isProcessing && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setOpenMenuId(null)}
                        />
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-20">
                          {!method.isDefault && (
                            <button
                              onClick={() => handleSetDefault(method._id)}
                              disabled={isSettingDefault}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                              <CheckCircle className="w-4 h-4 text-gray-400" />
                              Set as Default
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(method._id)}
                            disabled={isDeletingPayment}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Note */}
        {paymentMethods.length > 0 && (
          <div className="mt-4 p-3 bg-purple-50 border border-purple-100 rounded-xl flex items-start gap-3">
            <CreditCard className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-purple-700">
              Payments are processed weekly on Fridays. Your default payment
              method will be used for all payouts.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethods;
